import { useTranslate } from "../../../../../language/i18n";
import styles from "./ProductGroup.module.scss";
import ProductGroupRow, { ProductGroupItem } from "./ProductGroupRow";

type ProductGroupType = {
  id?: string;
  name?: string;
  hidePrices?: boolean;
  items?: ProductGroupItem[];
};

type Props = {
  productGroup?: ProductGroupType;
  currentSequenceNumber?: number;
  currentInStock?: boolean;
  currentIsOrderBlocked?: boolean;
  currency?: string;
  decimals?: number;
};

const isCurrentOrderable = (inStock?: boolean, isOrderBlocked?: boolean): boolean =>
  inStock ? !isOrderBlocked : false;

const resolveDisplayItems = (
  groupItems: ProductGroupItem[],
  currentSequenceNumber: number | undefined,
  currentIsOrderable: boolean,
): ProductGroupItem[] => {
  const otherSequenced = groupItems.filter(
    item => item.sequenceNumber !== undefined && item.sequenceNumber !== currentSequenceNumber,
  );

  const lower = otherSequenced.filter(
    item => currentSequenceNumber !== undefined && (item.sequenceNumber ?? 0) < currentSequenceNumber,
  );

  if (lower.length) return lower;
  if (currentIsOrderable || !otherSequenced.length) return [];
  return [...otherSequenced].sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0)).slice(0, 1);
};

const ProductGroup = ({
  productGroup,
  currentSequenceNumber,
  currentInStock,
  currentIsOrderBlocked,
  currency,
  decimals,
}: Props) => {
  const translate = useTranslate();

  const items = resolveDisplayItems(
    productGroup?.items ?? [],
    currentSequenceNumber,
    isCurrentOrderable(currentInStock, currentIsOrderBlocked),
  );

  if (!items.length) {
    return <p className={styles.empty}>{translate("NO_OPTIONS_AVAILABLE")}</p>;
  }

  const showPrice = !productGroup?.hidePrices;

  const listClass = `${styles.list} ${showPrice ? styles.threeCol : styles.twoCol}`;

  return (
    <div className={listClass}>
      <div className={styles.header}>
        <div></div>
        <div className={styles.numericHeader}>{translate("AVAILABLE_BALANCE")}</div>
        {showPrice && <div className={styles.numericHeader}>{translate("MAX_PRICE")}</div>}
      </div>
      {items.map(item => (
        <ProductGroupRow
          key={item.itemNumber}
          item={item}
          showPrice={showPrice}
          currency={currency}
          decimals={decimals}
        />
      ))}
    </div>
  );
};

export default ProductGroup;
